import React, { useState, useEffect } from 'react';
import { Icon } from '../Icon';
import { formatCurrency } from '../../utils/format';
import './ReceiptItemsEditor.css';

export interface ReceiptItem {
  name: string;
  amount: number;
  quantity?: number;
  unit_price?: number;
  category_hint?: string;
}

interface ReceiptItemsEditorProps {
  items: ReceiptItem[];
  currency: string;
  onItemsChange: (items: ReceiptItem[]) => void;
  onTotalAmountChange: (total: number) => void;
}

export const ReceiptItemsEditor: React.FC<ReceiptItemsEditorProps> = ({
  items,
  currency,
  onItemsChange,
  onTotalAmountChange
}) => {
  const [editableItems, setEditableItems] = useState<ReceiptItem[]>(items);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  // Безопасный парсер чисел из инпута (поддержка пустого значения и запятой)
  const toOptionalNumber = (raw: any): number | undefined => {
    if (raw === '' || raw === null || raw === undefined) return undefined;
    const num = typeof raw === 'number' ? raw : parseFloat(String(raw).replace(',', '.'));
    return Number.isFinite(num) ? num : undefined;
  };

  useEffect(() => {
    setEditableItems(items);
  }, [items]);

  // Автоматический пересчет общей суммы
  useEffect(() => {
    const total = editableItems.reduce((sum, item) => sum + (item.amount || 0), 0);
    onTotalAmountChange(total);
  }, [editableItems, onTotalAmountChange]);

  const handleItemChange = (index: number, field: keyof ReceiptItem, value: any) => {
    const newItems = [...editableItems];
    newItems[index] = { ...newItems[index], [field]: value };

    // Если изменили quantity или unit_price, пересчитываем amount всегда
    if (field === 'quantity' || field === 'unit_price') {
      const item = newItems[index];
      // Если quantity не указано, считаем как 1 (по умолчанию)
      const qty = (item.quantity !== undefined && item.quantity !== null && item.quantity > 0) 
        ? item.quantity 
        : 1;
      const unit = item.unit_price ?? 0;
      const calc = qty * unit;
      newItems[index].amount = Number.isFinite(calc) ? parseFloat(calc.toFixed(2)) : 0;
    }

    setEditableItems(newItems);
    onItemsChange(newItems);
  };

  const handleDeleteItem = (index: number) => {
    const newItems = editableItems.filter((_, i) => i !== index);
    setEditableItems(newItems);
    onItemsChange(newItems);
  };

  const handleAddItem = () => {
    const newItem: ReceiptItem = {
      name: '',
      amount: 0,
      quantity: 1, // По умолчанию 1 штука
      unit_price: 0
    };
    const newItems = [...editableItems, newItem];
    setEditableItems(newItems);
    onItemsChange(newItems);
    setEditingIndex(newItems.length - 1);
    setIsExpanded(true);
  };

  const totalAmount = editableItems.reduce((sum, item) => sum + (item.amount || 0), 0);

  if (editableItems.length === 0) {
    return null;
  }

  return (
    <div className="receipt-items-editor">
      <div className="receipt-items-editor__header" onClick={() => setIsExpanded(!isExpanded)}>
        <div className="receipt-items-editor__title">
          <span>📋 Список покупок ({editableItems.length})</span>
          <Icon name={isExpanded ? 'chevron-up' : 'chevron-down'} size={20} />
        </div>
        <div className="receipt-items-editor__total">
          Итого: {formatCurrency(totalAmount, currency)}
        </div>
      </div>

      {isExpanded && (
        <div className="receipt-items-editor__content">
          {editableItems.map((item, index) => (
            <div key={index} className="receipt-item">
              {/* Название товара */}
              <div className="receipt-item__row">
                <input
                  type="text"
                  className="receipt-item__input receipt-item__input--name"
                  value={item.name}
                  onChange={(e) => handleItemChange(index, 'name', e.target.value)}
                  placeholder="Название товара"
                  onFocus={() => setEditingIndex(index)}
                  onBlur={() => setEditingIndex(null)}
                />
                <button
                  className="receipt-item__delete"
                  onClick={() => handleDeleteItem(index)}
                  title="Удалить товар"
                >
                  <Icon name="trash" size={16} color="#FF3B30" />
                </button>
              </div>

              {/* Количество и цена */}
              <div className="receipt-item__details">
                <div className="receipt-item__field">
                  <label>Кол-во</label>
                  <input
                    type="number"
                    className="receipt-item__input receipt-item__input--number"
                    value={item.quantity ?? ''}
                    onChange={(e) => handleItemChange(index, 'quantity', toOptionalNumber(e.target.value))}
                    placeholder="1"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="receipt-item__field">
                  <label>Цена/шт</label>
                  <input
                    type="number"
                    className="receipt-item__input receipt-item__input--number"
                    value={item.unit_price ?? ''}
                    onChange={(e) => handleItemChange(index, 'unit_price', toOptionalNumber(e.target.value))}
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </div>

                <div className="receipt-item__field receipt-item__field--amount">
                  <label>Сумма</label>
                  <input
                    type="number"
                    className="receipt-item__input receipt-item__input--number receipt-item__input--amount"
                    value={item.amount ?? ''}
                    readOnly
                    disabled
                    placeholder="0"
                    step="0.01"
                    min="0"
                  />
                </div>
              </div>
            </div>
          ))}

          {/* Кнопка добавления нового товара */}
          <button className="receipt-items-editor__add-btn" onClick={handleAddItem}>
            <Icon name="add" size={18} />
            <span>Добавить товар</span>
          </button>

          {/* Итоговая сумма */}
          <div className="receipt-items-editor__summary">
            <div className="receipt-items-editor__summary-row">
              <span>Итого ({editableItems.length} {editableItems.length === 1 ? 'товар' : editableItems.length < 5 ? 'товара' : 'товаров'}):</span>
              <span className="receipt-items-editor__summary-amount">
                {formatCurrency(totalAmount, currency)}
              </span>
            </div>
          </div>

          {/* Подсказка */}
          <div className="receipt-items-editor__hint">
            💡 Изменения автоматически обновят общую сумму транзакции
          </div>
        </div>
      )}
    </div>
  );
};

